import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ProfileMakeItem } from 'fym-shared/src/music24/artistProfile';
import { useMakeProfileAssetsMutation, useUseReleaseForProfileMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { studioActionErrorFeedback, type StudioActionFeedback } from '../studio/studioActionErrorText';

export interface ProfileMake {
  /** Makes the items; true when the server took them (201). */
  make: (items: ProfileMakeItem[]) => Promise<boolean>;
  /** "Use this release for my profile" / "Use for my profile" (`from-release`). */
  fromRelease: (catalogItemUuid: string, kitAssetUuid?: string) => Promise<boolean>;
  isBusy: boolean;
  /** The answer of the last call that made nothing: it shows next to its button (`StudioActionError`). */
  feedback: StudioActionFeedback | null;
  clearFeedback: () => void;
}

/**
 * The make calls of the Profile (`POST .../artist-profile/assets` and `.../from-release`). A
 * refusal with an `M24_*` code keeps the sentence of the server (for example
 * `M24_PROFILE_SOURCE_INVALID`); any other error gets the general sentence. 0 credits.
 */
export function useProfileMake(accountId: number): ProfileMake {
  const intl = useIntl();
  const [makeAssets, makeState] = useMakeProfileAssetsMutation();
  const [postFromRelease, releaseState] = useUseReleaseForProfileMutation();
  const [feedback, setFeedback] = useState<StudioActionFeedback | null>(null);

  const finish = useCallback(
    (action: string, result: Awaited<ReturnType<typeof makeAssets>>): boolean => {
      if (isSuccessfulResult(result)) {
        setFeedback(null);
        return true;
      }
      setFeedback(studioActionErrorFeedback(intl, action, result.error));
      return false;
    },
    [intl]
  );

  const make = useCallback(
    async (items: ProfileMakeItem[]): Promise<boolean> => {
      setFeedback(null);
      return finish('profile make', await makeAssets({ accountId, items }));
    },
    [accountId, finish, makeAssets]
  );

  const fromRelease = useCallback(
    async (catalogItemUuid: string, kitAssetUuid?: string): Promise<boolean> => {
      setFeedback(null);
      return finish('profile from release', await postFromRelease({ accountId, catalogItemUuid, kitAssetUuid }));
    },
    [accountId, finish, postFromRelease]
  );

  const clearFeedback = useCallback(() => setFeedback(null), []);

  const isBusy = makeState.isLoading || releaseState.isLoading;
  return useMemo(
    () => ({ make, fromRelease, isBusy, feedback, clearFeedback }),
    [clearFeedback, feedback, fromRelease, isBusy, make]
  );
}
